import { useQueryClient } from "@tanstack/react-query";
import { useMediaQuery } from "@uidotdev/usehooks";
import { useChannel } from "ably/react";
import { useEffect, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCallLogs } from "../../contexts/CallLogsContext";
import { useCalls } from "../../contexts/CallsContext";
import { useDevices } from "../../contexts/DevicesContext";
import { usePhoneControl } from "../../contexts/PhoneControlContext";
import { useSoftphone } from "../../contexts/SoftphoneContext";
import { TopbarSearch } from "../../contexts/TopbarContext";
import useMessages from "../../hooks/useMessages";
import sidekickLogo from "../../images/sidekick-logo-white-75x79.png";
import layout from "../../layout";
import logo from "../../logos/icon_white.svg";
import { colors, fonts } from "../../style";
import AcdActions from "../AcdActions";
import { Icon } from "../ui";
import {
  DOCS_URL,
  ModeIndicator,
  PresenceDropdown,
  UserMenu,
} from "./UserControls";

export const NARROW_QUERY = "(max-width: 860px)";
// Keep in sync with .cx-call-minimized in index.css
const MINIMIZED_CALL_HEIGHT = 220;

const style = {
  shell: {
    display: "flex",
    height: "100vh",
    background: colors.pageBackground,
  },
  sidebar: {
    flex: `0 0 ${layout.sidebarWidth}`,
    width: layout.sidebarWidth,
    height: "100vh",
    boxSizing: "border-box",
    background: colors.sidebar,
    borderRight: `1px solid ${colors.border}`,
    padding: "20px 12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflowY: "auto",
    fontFamily: fonts.system,
  },
  drawer: {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 900,
    boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 10px 18px",
    fontWeight: 700,
    fontSize: "16px",
    color: colors.text,
    textDecoration: "none",
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    flex: "none",
    boxSizing: "border-box",
    height: layout.topbarHeight,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "0 16px",
    background: colors.pageBackground,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: fonts.system,
  },
  content: {
    width: "100%",
    height: `calc(100vh - ${layout.topbarHeight})`,
    overflowY: "auto",
  },
  iconButton: {
    position: "relative",
    border: 0,
    background: "none",
    color: colors.textStrong,
    padding: "6px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
  },
  navItem: (active) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 10px",
    borderRadius: "8px",
    color: active ? colors.text : "rgba(255, 255, 255, 0.72)",
    background: active ? colors.raised : "none",
    fontSize: "14px",
    textDecoration: "none",
  }),
  badge: {
    marginLeft: "auto",
    minWidth: "20px",
    padding: "1px 6px",
    borderRadius: "999px",
    background: colors.red,
    color: colors.text,
    fontSize: "11px",
    fontWeight: 700,
    textAlign: "center",
  },
  dot: {
    marginLeft: "auto",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: colors.red,
  },
};

function AppShell({ children }) {
  const { isPhoneControl, isAcd, apiUser } = useAuth();
  const { audioAllowed } = useDevices();
  const { pathname } = useLocation();
  const narrow = useMediaQuery(NARROW_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { missedCallCount } = useCallLogs();
  const {
    hasAnyMessageFeatures,
    hasAnyUnreadMessages,
    hasEngageFeature,
    hasWhatsappFeature,
  } = useMessages();
  const unreadDot = hasAnyUnreadMessages && !matchPath("/messages/*", pathname);
  // Badges live in the drawer on narrow screens, so flag them on its button
  const menuAlert = missedCallCount > 0 || unreadDot;

  useEffect(() => setDrawerOpen(false), [pathname, narrow]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const sidebar = (
    <Sidebar
      style={narrow ? { ...style.sidebar, ...style.drawer } : style.sidebar}
      hasAnyMessageFeatures={hasAnyMessageFeatures}
      unreadDot={unreadDot}
      missedCallCount={missedCallCount}
      // Also close on links to the current route, which don't change pathname
      onLinkClick={narrow ? () => setDrawerOpen(false) : undefined}
    />
  );

  return (
    <div style={style.shell}>
      {apiUser?.has_ably && (
        <AblyChannelHandler
          hasEngageFeature={hasEngageFeature}
          hasWhatsappFeature={hasWhatsappFeature}
        />
      )}

      {narrow
        ? drawerOpen && (
            <>
              <div
                onClick={() => setDrawerOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 899,
                  background: "rgba(10, 10, 18, 0.6)",
                }}
              />
              {sidebar}
            </>
          )
        : sidebar}

      <div style={style.main}>
        <header style={style.toolbar}>
          {narrow && (
            <button
              type="button"
              aria-label="Open menu"
              style={style.iconButton}
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="menu" size={20} />
              {menuAlert && (
                <span
                  aria-label="New activity"
                  style={{
                    ...style.dot,
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                  }}
                />
              )}
            </button>
          )}
          {narrow && (
            <Link to="/" aria-label="Connect" style={{ display: "flex" }}>
              <img src={logo} alt="" style={{ height: "22px" }} />
            </Link>
          )}
          <div style={{ flex: 1, minWidth: 0, maxWidth: "560px" }}>
            <TopbarSearch />
          </div>
          <div style={{ flex: 1 }} />
          {isAcd && <AcdActions />}
          {narrow && (
            <div style={{ flex: "none", width: "128px" }}>
              <PresenceDropdown upward={false} />
            </div>
          )}
        </header>

        <div style={style.content}>
          {!isPhoneControl && !audioAllowed && (
            <div
              role="alert"
              style={{
                padding: "12px 16px",
                background: colors.red,
                color: colors.text,
                fontSize: "14px",
              }}
            >
              CONNECT doesn't have permissions to audio devices. You will need
              to update your browser settings to allow CONNECT to use your audio
              devices.
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}

function Sidebar({
  style: sidebarStyle,
  hasAnyMessageFeatures,
  unreadDot,
  missedCallCount,
  onLinkClick,
}) {
  const { hasFeature, hasIntegration, apiUser, isPhoneControl } = useAuth();
  const { hasActiveCall } = useCalls();
  const softphone = useSoftphone();
  const phoneControl = usePhoneControl();
  const callMinimized = isPhoneControl
    ? phoneControl?.minimized
    : softphone?.minimized;
  // Phone control shows the call pane while connecting, before activeCall
  const callShown =
    hasActiveCall() || (isPhoneControl && !!phoneControl?.connecting);
  // The minimised call pane docks over the bottom of the sidebar
  const callDocked = callShown && callMinimized;

  return (
    <nav
      aria-label="Main"
      style={sidebarStyle}
      onClick={(e) => onLinkClick && e.target.closest("a") && onLinkClick()}
    >
      <Link to="/" style={style.brand}>
        <img src={logo} alt="" style={{ height: "22px" }} />
        CONNECT
      </Link>

      <NavItem to="/" end icon="contacts">
        Contacts
      </NavItem>
      {hasAnyMessageFeatures && (
        <NavItem to="/messages" icon="inbox" dot={unreadDot}>
          Messages
        </NavItem>
      )}
      <NavItem
        to="/calls"
        icon="phone"
        badge={missedCallCount > 0 ? missedCallCount : null}
      >
        Calls
      </NavItem>
      {hasIntegration(["microsoft", "google"]) && (
        <NavItem to="/calendar" icon="calendar">
          Calendar
        </NavItem>
      )}
      {hasIntegration(["asana", "clickup"]) && (
        <NavItem to="/projects" icon="list">
          Projects
        </NavItem>
      )}
      {hasIntegration(["clockify"]) && (
        <NavItem to="/clockify" icon="clock">
          Clockify
        </NavItem>
      )}
      {hasFeature(["insights"]) && (
        <NavItem to="/insights" icon="insights">
          Insights
        </NavItem>
      )}
      {apiUser?.has_sidekick && apiUser?.sidekick_configured && (
        <NavItem
          to="/sidekick"
          image={
            <img
              src={sidekickLogo}
              alt=""
              style={{ width: "17px", height: "17px", flex: "none" }}
            />
          }
        >
          Sidekick
        </NavItem>
      )}

      <NavItem to="/apps" icon="apps">
        Apps
      </NavItem>

      <div style={{ flex: 1, minHeight: "16px" }} />

      {/* Same slots as Sidekick web: help (its search), settings (its
          admin), then the user button. Presence and mode are Connect only. */}
      <a
        href={DOCS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="sk-nav"
        style={{
          ...style.navItem(false),
          fontSize: "13px",
          color: colors.textMuted,
        }}
      >
        <Icon name="help" size={15} />
        Help
      </a>
      <NavItem to="/settings" icon="settings">
        Settings
      </NavItem>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginTop: "8px",
          paddingTop: "10px",
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <PresenceDropdown />
        <ModeIndicator />
      </div>

      <UserMenu />

      {callDocked && (
        <div style={{ flex: "none", height: `${MINIMIZED_CALL_HEIGHT}px` }} />
      )}
    </nav>
  );
}

function NavItem({ to, end, icon, image, badge, dot, children }) {
  const { pathname } = useLocation();
  const active = !!matchPath({ path: end ? to : `${to}/*`, end }, pathname);

  return (
    <Link
      to={to}
      className="sk-nav"
      aria-current={active ? "page" : undefined}
      style={style.navItem(active)}
    >
      {image ?? <Icon name={icon} size={17} />}
      {children}
      {badge != null && <span style={style.badge}>{badge}</span>}
      {dot && <span style={style.dot} aria-label="Unread messages" />}
    </Link>
  );
}

// Always mounted (not inside the nav) so the drawer closing on narrow
// screens doesn't drop the subscriptions
function AblyChannelHandler({ hasEngageFeature, hasWhatsappFeature }) {
  const { apiUser } = useAuth();
  const queryClient = useQueryClient();

  useChannel(
    {
      channelName: `whatsapp:${apiUser.customer_id}:notifications`,
      skip: !hasWhatsappFeature,
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["whatsapp-threads"],
      });
    },
  );

  useChannel(
    {
      channelName: `chat:${apiUser.engage_id}:notifications`,
      skip: !hasEngageFeature,
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["engage-chats"],
      });
    },
  );

  return null;
}

export default AppShell;
